import { API_ROOT } from '../../config';
import { getOS } from '../../modules/AppInfo';
import Currency from '../../modules/Currency';
import { ssFetchJSON } from '../../modules/ssFetch';
import { ICard } from '../../store/PaymentMethods';
import { PaymentOption } from '../Providers/Appointments';
import type { CheckoutDetails } from '../../store/ProviderManageAppointment/ProviderManageAppointment.model';

export type CheckoutPayloadParams = {
  'form-0-amount': string;
  'form-0-card'?: string;
  'form-0-description': string;
  'form-0-typ': string;
  'form-INITIAL_FORMS': string;
  'form-MAX_NUM_FORMS': string;
  'form-TOTAL_FORMS': string;
  cost: string;
  creation_source: string;
  discount_code: string;
  do_not_send_client_receipt: false;
  product_cost: string | number;
  signature: string;
  tip: string | number;
};

interface CheckoutSuccessResponse {
  status: 'OK';
  token: string;
  callback: false;
  appointment_id: number;
}

interface CheckoutPaymentErrorResponse {
  status: 'ERROR';
  index: number | null;
  'error_message': string;
  'token': string;
  'callback': false;
}

type CheckoutErrorResponse = {
  __all__: string[];
} | Record<string, string[]>;

type CheckoutResponse = (
  CheckoutSuccessResponse | CheckoutPaymentErrorResponse | CheckoutErrorResponse
);

export function isCheckoutSuccess(response: unknown): response is CheckoutSuccessResponse {
  return (
    typeof response === 'object'
    && 'status' in response
    && (response as CheckoutSuccessResponse | CheckoutPaymentErrorResponse).status === 'OK'
  );
}

export function isCheckoutPaymentError(
  response: unknown,
): response is CheckoutPaymentErrorResponse {
  return (
    typeof response === 'object'
    && 'status' in response
    && (response as CheckoutSuccessResponse | CheckoutPaymentErrorResponse).status === 'ERROR'
  );
}

type CheckoutPayload = CheckoutDetails & {
  paymentType: PaymentOption;
  card?: ICard;
  signature?: string;
};

export function buildCheckoutParams(checkout: CheckoutPayload): CheckoutPayloadParams {
  const checkoutParams: Partial<CheckoutPayloadParams> = {
    'form-INITIAL_FORMS': '0',
    'form-MAX_NUM_FORMS': '',
    'form-0-description': '',
    signature: checkout.signature,
    product_cost: checkout.product_cost,
    do_not_send_client_receipt: false,
    creation_source: getOS(),
  };

  checkoutParams.cost = (
    Currency.parse(checkout.cost) - Currency.parse(checkout.prepayTotal)
  ).toString();

  if (Number(checkout?.tip) > 0) {
    checkoutParams.tip = checkout.tip;
  }

  if (checkout.discountCode) {
    checkoutParams.discount_code = checkout.discountCode;
  }

  const total = parseFloat(checkout.totalBeforeDiscount.toString()).toFixed(2);

  checkoutParams['form-TOTAL_FORMS'] = '1';
  checkoutParams['form-0-amount'] = total;
  checkoutParams['form-0-typ'] = checkout.paymentType;

  if (checkout.paymentType === 'stripe_connect') {
    checkoutParams['form-0-card'] = checkout.card.id.toString();
  }

  return checkoutParams as CheckoutPayloadParams;
}

export function checkoutAppointment(
  appointmentId: number,
  checkout: CheckoutPayload,
): Promise<CheckoutResponse> {
  const checkoutParams = buildCheckoutParams(checkout);
  const url = new URL(`/checkout/appointment/${appointmentId}`, API_ROOT);
  return ssFetchJSON<CheckoutResponse>(url.toString(), {
    method: 'POST',
    form: checkoutParams,
  });
}
